import React from 'react'
import { Label } from '../label/Label'
import { Hint } from '../hint/Hint'
import { lit } from 'src/utils'

export type TextareaProps = React.JSX.IntrinsicElements['textarea'] & {
    /**
     * The class name to be used for styling purposes
     */
    className?: string

    /**
     * The class name to be used to hook GA event tracking
     */
    analyticsClassName?: string

    /**
     * The class name to be used for styling div that contains the input
     */
    containerClass?: string

    /**
     * Class name for font size
     */
    fontSizeClass?: string

    /**
     * The name of the input -- this is required to identify the input
     * in the form data.
     * The name will be used as an ID, if ID is not provided.
     */
    name: string

    /**
     * The initial value of the input -- this is optional.
     */
    value?: string

    /**
     * The placeholder is used to provide a hint as to
     * what an inputted value should look like, not an explanation or prompt.
     */
    placeholder?: string

    /**
     * The label elements is useful for assistive technologies such as screen readers.
     */
    label: string

    /**
     * Allows placement of the label element in the input field or above it.
     */
    labelPlacement?: 'inline' | 'above'

    hasError?: boolean

    /**
     * The hint -- a helper text to provide more information about the input.
     */
    hint?: string

    /**
     * This indicates whether a input field is required -- this is optional.
     */
    required?: boolean

    /**
     * This specifies how many rows the textarea should be -- default is 4
     */
    rows?: number
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    (props, ref) => {
        const {
            className,
            analyticsClassName = 'js_Textarea',
            containerClass = '',
            fontSizeClass = 'text-base',
            rows = 4,
            label,
            labelPlacement,
            placeholder,
            name,
            hasError,
            hint,
            required,
            id,
            ...rest
        } = props

        const ariaDescribeRequired = required ? `${name}-required` : ''
        const ariaDescribeHint = hint ? `${name}-hint` : ''
        const helperClass = hasError
            ? 'border-state-error'
            : 'border-neutral-300'
        const labelIsInline = labelPlacement === 'inline'

        return (
            <div>
                {labelPlacement === 'above' && (
                    <Label
                        fontSize={fontSizeClass}
                        htmlFor={id || name}
                        required={required}
                    >
                        {label}
                    </Label>
                )}
                <div className={containerClass}>
                    <textarea
                        ref={ref}
                        rows={rows}
                        name={name}
                        id={id || name}
                        placeholder={labelIsInline ? label : placeholder}
                        aria-label={labelIsInline ? label : undefined}
                        {...rest}
                        className={lit`form-input focus:border-state-focus focus:ring-state-focus block w-full border-neutral-300 leading-8 ${helperClass} ${className} ${analyticsClassName}`}
                        aria-describedby={`${ariaDescribeRequired} ${ariaDescribeHint}`}
                    />
                    {hint && <Hint id={ariaDescribeHint}>{hint}</Hint>}
                </div>
            </div>
        )
    }
)

Textarea.displayName = 'Textarea'
